import type { DateRange } from '@ecom/contracts';
import type { AdminLocale } from '@/i18n';

/**
 * Calendar days for a date range (docs/07-Admin/Reports.md).
 *
 * A range is two `YYYY-MM-DD` days, and "today" is the day on the operator's
 * own calendar — not the UTC day `toISOString()` gives, which in Riyadh is
 * still yesterday until three in the morning. Once today is known, the
 * arithmetic runs on the day strings in UTC, so no zone or clock change can
 * move a day.
 */

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A complete `YYYY-MM-DD` — what a date input holds once a whole day is typed. */
export function isDay(value: string): boolean {
  return DAY.test(value) && !Number.isNaN(new Date(`${value}T00:00:00Z`).getTime());
}

/** The calendar day it is at `now` in `timeZone` — the browser's own zone when none is given. */
export function localDay(now: Date = new Date(), timeZone?: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? '';

  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** `days` later (or earlier, when negative) on the calendar. */
export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** The last `days` days, today included: "last 7 days" ends today and starts six days ago. */
export function lastDays(days: number, now: Date = new Date(), timeZone?: string): DateRange {
  const to = localDay(now, timeZone);
  return { from: addDays(to, -(days - 1)), to };
}

/** This month so far: the first of the month to today. */
export function monthToDate(now: Date = new Date(), timeZone?: string): DateRange {
  const to = localDay(now, timeZone);
  return { from: `${to.slice(0, 8)}01`, to };
}

/** The shortcuts a range picker offers, by name. */
export const RANGE_PRESETS = {
  last7: (now?: Date, timeZone?: string) => lastDays(7, now, timeZone),
  last30: (now?: Date, timeZone?: string) => lastDays(30, now, timeZone),
  month: (now?: Date, timeZone?: string) => monthToDate(now, timeZone),
} as const;

export type RangePreset = keyof typeof RANGE_PRESETS;

/** Which shortcut a range is, if any — so the picker can show the one in force. */
export function presetOf(range: DateRange, now: Date = new Date(), timeZone?: string): RangePreset | null {
  for (const id of Object.keys(RANGE_PRESETS) as RangePreset[]) {
    const preset = RANGE_PRESETS[id](now, timeZone);
    if (preset.from === range.from && preset.to === range.to) return id;
  }
  return null;
}

/** The two ends in order — a "from" typed past the "to" means the same days, not none. */
export function ordered(range: DateRange): DateRange {
  return range.from <= range.to ? range : { from: range.to, to: range.from };
}

function calendar(locale: AdminLocale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-SA', { ...options, timeZone: 'UTC' });
}

/** A `YYYY-MM` month as a person says it: "October 2026", "أكتوبر ٢٠٢٦". */
export function monthLabel(month: string, locale: AdminLocale): string {
  const parsed = new Date(`${month}-01T00:00:00Z`);
  if (!/^\d{4}-\d{2}$/.test(month) || Number.isNaN(parsed.getTime())) return month;

  return calendar(locale, { month: 'long', year: 'numeric' }).format(parsed);
}

/** A `YYYY-MM-DD` day with its year: "1 Oct 2026" — a filter chip's date. */
export function dayLabel(day: string, locale: AdminLocale): string {
  if (!isDay(day)) return day;

  return calendar(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${day}T00:00:00Z`));
}
